/**
 * DB Diagram Page
 * Visual database schema diagram with tables, columns, and relationships
 * Similar to chartdb.io webapp
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useCollections } from '@/hooks/useCollections';
import { useFieldsQuery } from '@/hooks/useFieldsQuery';
import { useRelations } from '@/hooks/useRelations';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

import { Loader2, Key, Link as LinkIcon, ZoomIn, ZoomOut, Maximize2, Search, RotateCcw, Grid3X3, Database } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

// Types
interface TablePosition {
    x: number;
    y: number;
}

interface TableNode {
    collection: string;
    label: string;
    fields: FieldInfo[];
    position: TablePosition;
}

interface FieldInfo {
    name: string;
    type: string;
    isPrimary: boolean;
    isForeignKey: boolean;
    isNullable: boolean;
    relatedTable?: string;
    relatedField?: string;
}

interface RelationLine {
    from: { table: string; field: string };
    to: { table: string; field: string };
    type: 'one-to-one' | 'one-to-many' | 'many-to-many';
}

// Constants
const TABLE_WIDTH = 280;
const HEADER_HEIGHT = 40;
const ROW_HEIGHT = 32;
const INITIAL_SPACING = 320;

export function DBDiagramPage() {
    const { data: collections = [], isLoading: collectionsLoading } = useCollections();
    const { data: allFields = [], isLoading: fieldsLoading } = useFieldsQuery();
    const { data: relations = [], isLoading: relationsLoading } = useRelations();

    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 50, y: 50 });
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [tablePositions, setTablePositions] = useState<Record<string, TablePosition>>({});
    const [draggingTable, setDraggingTable] = useState<string | null>(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTable, setSelectedTable] = useState<string | null>(null);
    const [showSystemTables, setShowSystemTables] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    const isLoading = collectionsLoading || fieldsLoading || relationsLoading;

    // Process tables and fields
    const tables = useMemo((): TableNode[] => {
        if (!collections.length || !allFields.length) return [];

        return collections
            .filter(c => showSystemTables || !c.collection.startsWith('cms_')) // Optionally exclude system tables
            .map((collection, index) => {
                const collectionFields = allFields.filter(f => f.collection === collection.collection);

                const fields: FieldInfo[] = collectionFields.map(field => {
                    // Check if this field is a foreign key
                    const relation = relations.find(r =>
                        r.collection === collection.collection && r.field === field.field
                    );

                    return {
                        name: field.field,
                        type: field.type || field.schema?.data_type || 'unknown',
                        isPrimary: field.schema?.is_primary_key || false,
                        isForeignKey: !!relation,
                        isNullable: field.schema?.is_nullable !== false,
                        relatedTable: relation?.related_collection || undefined,
                        relatedField: relation?.meta?.one_field || 'id',
                    };
                });

                // Calculate initial position in a grid layout
                const cols = 4;
                const row = Math.floor(index / cols);
                const col = index % cols;

                return {
                    collection: collection.collection,
                    label: collection.meta?.collection || collection.collection,
                    fields,
                    position: tablePositions[collection.collection] || {
                        x: col * INITIAL_SPACING + 50,
                        y: row * 400 + 50,
                    },
                };
            });
    }, [collections, allFields, relations, tablePositions, showSystemTables]);

    // Process relation lines
    const relationLines = useMemo((): RelationLine[] => {
        if (!relations.length) return [];

        console.log('[DBDiagram] All relations:', relations);

        return relations
            .filter(r => {
                if (!r.related_collection) return false;
                // Filter based on showSystemTables setting
                if (!showSystemTables) {
                    if (r.collection.startsWith('cms_') || r.related_collection.startsWith('cms_')) {
                        return false;
                    }
                }
                return true;
            })
            .map(relation => {
                // Determine relationship type based on meta
                let type: 'one-to-one' | 'one-to-many' | 'many-to-many' = 'one-to-many';

                if (relation.meta?.junction_field) {
                    type = 'many-to-many';
                } else if (relation.meta?.one_field) {
                    type = 'one-to-one';
                }

                console.log('[DBDiagram] Relation line:', relation.collection, '.', relation.field, '->', relation.related_collection);

                return {
                    from: { table: relation.collection, field: relation.field },
                    to: { table: relation.related_collection!, field: 'id' },
                    type,
                };
            });
    }, [relations, showSystemTables]);

    // Filter tables by search
    const filteredTables = useMemo(() => {
        if (!searchQuery.trim()) return tables;
        const query = searchQuery.toLowerCase();
        return tables.filter(t =>
            t.collection.toLowerCase().includes(query) ||
            t.label.toLowerCase().includes(query) ||
            t.fields.some(f => f.name.toLowerCase().includes(query))
        );
    }, [tables, searchQuery]);

    // Get connected tables and fields for the selected table
    const connectionInfo = useMemo(() => {
        if (!selectedTable) return { tables: new Set<string>(), fields: new Map<string, Set<string>>() };

        const connectedTables = new Set<string>();
        const highlightedFields = new Map<string, Set<string>>();

        relationLines.forEach(line => {
            if (line.from.table === selectedTable) {
                connectedTables.add(line.to.table);
                // Add highlighted field for from table
                if (!highlightedFields.has(line.from.table)) highlightedFields.set(line.from.table, new Set());
                highlightedFields.get(line.from.table)!.add(line.from.field);
                // Add highlighted field for to table
                if (!highlightedFields.has(line.to.table)) highlightedFields.set(line.to.table, new Set());
                highlightedFields.get(line.to.table)!.add(line.to.field);
            } else if (line.to.table === selectedTable) {
                connectedTables.add(line.from.table);
                // Add highlighted field for from table
                if (!highlightedFields.has(line.from.table)) highlightedFields.set(line.from.table, new Set());
                highlightedFields.get(line.from.table)!.add(line.from.field);
                // Add highlighted field for to table
                if (!highlightedFields.has(line.to.table)) highlightedFields.set(line.to.table, new Set());
                highlightedFields.get(line.to.table)!.add(line.to.field);
            }
        });

        return { tables: connectedTables, fields: highlightedFields };
    }, [selectedTable, relationLines]);

    // Cluster-based table arrangement algorithm
    // 1. Find connected clusters (tables that relate to each other)
    // 2. Place each cluster together as a group
    // 3. Within each cluster, order by relationship hierarchy (parents left, children right)
    // 4. Standalone tables go at the end
    const getSmartTableOrder = useCallback(() => {
        // Build adjacency map (undirected - any connection counts)
        const connections = new Map<string, Set<string>>();
        tables.forEach(t => connections.set(t.collection, new Set()));

        relationLines.forEach(line => {
            connections.get(line.from.table)?.add(line.to.table);
            connections.get(line.to.table)?.add(line.from.table);
        });

        // Find connected clusters using BFS
        const visited = new Set<string>();
        const clusters: TableNode[][] = [];

        const bfs = (startTable: string): TableNode[] => {
            const cluster: TableNode[] = [];
            const queue = [startTable];

            while (queue.length > 0) {
                const current = queue.shift()!;
                if (visited.has(current)) continue;
                visited.add(current);

                const table = tables.find(t => t.collection === current);
                if (table) cluster.push(table);

                const neighbors = connections.get(current) || new Set();
                neighbors.forEach(neighbor => {
                    if (!visited.has(neighbor)) {
                        queue.push(neighbor);
                    }
                });
            }

            return cluster;
        };

        // Find all clusters (starting with tables that have the most connections)
        const tablesByConnections = [...tables].sort((a, b) =>
            (connections.get(b.collection)?.size || 0) - (connections.get(a.collection)?.size || 0)
        );

        tablesByConnections.forEach(table => {
            if (!visited.has(table.collection)) {
                const cluster = bfs(table.collection);
                if (cluster.length > 0) {
                    clusters.push(cluster);
                }
            }
        });

        // Sort clusters: larger clusters first, then standalone tables at the end
        clusters.sort((a, b) => {
            // Clusters with connections come before standalone tables
            if (a.length === 1 && b.length > 1) return 1;
            if (a.length > 1 && b.length === 1) return -1;
            // Larger clusters come first
            return b.length - a.length;
        });

        // Within each cluster, order by relationship hierarchy
        const orderedTables: TableNode[] = [];

        clusters.forEach(cluster => {
            if (cluster.length === 1 && cluster[0]) {
                // Standalone table - just add it
                orderedTables.push(cluster[0]);
                return;
            }

            // For connected clusters, order by relationship depth
            // Build directed refs for this cluster
            const outgoingRefs = new Map<string, Set<string>>();
            const incomingRefs = new Map<string, Set<string>>();

            cluster.forEach(t => {
                outgoingRefs.set(t.collection, new Set());
                incomingRefs.set(t.collection, new Set());
            });

            relationLines.forEach(line => {
                if (outgoingRefs.has(line.from.table) && outgoingRefs.has(line.to.table)) {
                    outgoingRefs.get(line.from.table)?.add(line.to.table);
                    incomingRefs.get(line.to.table)?.add(line.from.table);
                }
            });

            // Score: negative = parent (referenced by many), positive = child (references many)
            const getScore = (name: string) =>
                (outgoingRefs.get(name)?.size || 0) - (incomingRefs.get(name)?.size || 0);

            // Sort cluster: parents first (left), then children (right)
            const sortedCluster = [...cluster].sort((a, b) => {
                const scoreA = getScore(a.collection);
                const scoreB = getScore(b.collection);
                if (scoreA !== scoreB) return scoreA - scoreB;
                return b.fields.length - a.fields.length;
            });

            orderedTables.push(...sortedCluster);
        });

        return orderedTables;
    }, [tables, relationLines]);

    // Initialize table positions using cluster-based ordering
    useEffect(() => {
        if (tables.length > 0 && Object.keys(tablePositions).length === 0) {
            const positions: Record<string, TablePosition> = {};
            const maxRows = tables.length > 12 ? 3 : 2;
            const orderedTables = getSmartTableOrder();

            // Calculate row heights
            const cols = Math.ceil(orderedTables.length / maxRows);
            const rowHeights: number[] = [];
            for (let row = 0; row < maxRows; row++) {
                let maxHeight = 0;
                for (let col = 0; col < cols; col++) {
                    const index = col * maxRows + row;
                    const table = orderedTables[index];
                    if (table) {
                        const tableHeight = HEADER_HEIGHT + table.fields.length * ROW_HEIGHT + 20;
                        maxHeight = Math.max(maxHeight, tableHeight);
                    }
                }
                rowHeights.push(maxHeight || 400);
            }

            orderedTables.forEach((table, index) => {
                const col = Math.floor(index / maxRows);
                const row = index % maxRows;

                let yPos = 50;
                for (let r = 0; r < row; r++) {
                    yPos += (rowHeights[r] ?? 400) + 30;
                }

                positions[table.collection] = {
                    x: col * INITIAL_SPACING + 50,
                    y: yPos,
                };
            });
            setTablePositions(positions);
        }
    }, [tables, tablePositions, getSmartTableOrder]);

    // Handle zoom
    const handleZoom = useCallback((delta: number) => {
        setZoom(prev => Math.min(Math.max(prev + delta, 0.25), 2));
    }, []);

    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        // Zoom on scroll
        handleZoom(e.deltaY > 0 ? -0.1 : 0.1);
    }, [handleZoom]);

    // Handle pan
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.target === containerRef.current || e.target === svgRef.current) {
            setIsPanning(true);
            setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        }
    }, [pan]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isPanning) {
            setPan({
                x: e.clientX - panStart.x,
                y: e.clientY - panStart.y,
            });
        } else if (draggingTable) {
            const container = containerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const x = (e.clientX - rect.left - pan.x) / zoom - dragOffset.x;
            const y = (e.clientY - rect.top - pan.y) / zoom - dragOffset.y;

            setTablePositions(prev => ({
                ...prev,
                [draggingTable]: { x: Math.max(0, x), y: Math.max(0, y) },
            }));
        }
    }, [isPanning, panStart, draggingTable, dragOffset, pan, zoom]);

    const handleMouseUp = useCallback(() => {
        setIsPanning(false);
        setDraggingTable(null);
    }, []);

    // Handle table drag
    const handleTableMouseDown = useCallback((e: React.MouseEvent, tableName: string) => {
        e.stopPropagation();
        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const tablePos = tablePositions[tableName] || { x: 0, y: 0 };

        setDraggingTable(tableName);
        setDragOffset({
            x: (e.clientX - rect.left - pan.x) / zoom - tablePos.x,
            y: (e.clientY - rect.top - pan.y) / zoom - tablePos.y,
        });
    }, [tablePositions, pan, zoom]);

    // Reset view
    const resetView = useCallback(() => {
        setZoom(1);
        setPan({ x: 50, y: 50 });
    }, []);

    // Auto-arrange using cluster-based smart ordering
    const autoArrange = useCallback(() => {
        const positions: Record<string, TablePosition> = {};
        const maxRows = tables.length > 12 ? 3 : 2;
        const orderedTables = getSmartTableOrder();

        // Calculate row heights based on tallest table in each row
        const cols = Math.ceil(orderedTables.length / maxRows);
        const rowHeights: number[] = [];
        for (let row = 0; row < maxRows; row++) {
            let maxHeight = 0;
            for (let col = 0; col < cols; col++) {
                const index = col * maxRows + row;
                const table = orderedTables[index];
                if (table) {
                    const tableHeight = HEADER_HEIGHT + table.fields.length * ROW_HEIGHT + 20;
                    maxHeight = Math.max(maxHeight, tableHeight);
                }
            }
            rowHeights.push(maxHeight || 400);
        }

        // Position tables
        orderedTables.forEach((table, index) => {
            const col = Math.floor(index / maxRows);
            const row = index % maxRows;

            let yPos = 50;
            for (let r = 0; r < row; r++) {
                yPos += (rowHeights[r] ?? 400) + 30;
            }

            positions[table.collection] = {
                x: col * INITIAL_SPACING + 50,
                y: yPos,
            };
        });

        setTablePositions(positions);
        setPan({ x: 50, y: 50 });
    }, [tables, getSmartTableOrder]);

    // Calculate line path between tables - handles all positioning scenarios
    const getLinePath = useCallback((from: TableNode, fromField: string, to: TableNode, toField: string): { path: string; startX: number; startY: number; endX: number; endY: number } => {
        const fromPos = tablePositions[from.collection] || from.position;
        const toPos = tablePositions[to.collection] || to.position;

        const fromFieldIndex = from.fields.findIndex(f => f.name === fromField);
        const toFieldIndex = to.fields.findIndex(f => f.name === toField);

        // Calculate Y positions (center of field row)
        const fromY = fromPos.y + HEADER_HEIGHT + (Math.max(0, fromFieldIndex) + 0.5) * ROW_HEIGHT;
        const toY = toPos.y + HEADER_HEIGHT + (Math.max(0, toFieldIndex) + 0.5) * ROW_HEIGHT;

        // Determine which side to connect from/to based on relative positions
        const fromCenterX = fromPos.x + TABLE_WIDTH / 2;
        const toCenterX = toPos.x + TABLE_WIDTH / 2;

        let fromX: number, toX: number;

        if (fromCenterX < toCenterX) {
            // From table is to the left of To table
            fromX = fromPos.x + TABLE_WIDTH; // Right side of from
            toX = toPos.x; // Left side of to
        } else {
            // From table is to the right of To table
            fromX = fromPos.x; // Left side of from
            toX = toPos.x + TABLE_WIDTH; // Right side of to
        }

        // Calculate control points for a smooth curve
        const dx = Math.abs(toX - fromX);
        const dy = Math.abs(toY - fromY);
        const controlOffset = Math.max(Math.min(dx / 2, 80), 30);

        // Create bezier curve path
        let path: string;
        if (fromX < toX) {
            // Normal left-to-right connection
            path = `M ${fromX} ${fromY} C ${fromX + controlOffset} ${fromY}, ${toX - controlOffset} ${toY}, ${toX} ${toY}`;
        } else {
            // Right-to-left connection (need to curve around)
            const midY = (fromY + toY) / 2;
            const offsetY = dy < 50 ? 50 : 0;
            path = `M ${fromX} ${fromY} C ${fromX - controlOffset} ${fromY}, ${fromX - controlOffset} ${midY - offsetY}, ${(fromX + toX) / 2} ${midY} S ${toX + controlOffset} ${toY}, ${toX} ${toY}`;
        }

        return { path, startX: fromX, startY: fromY, endX: toX, endY: toY };
    }, [tablePositions]);

    if (isLoading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center h-96">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="h-[calc(100vh-200px)] m-0 p-0 gap-1">
            <CardHeader className="mt-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search tables..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 w-48 h-9"
                            />
                        </div>

                        {/* Zoom controls */}
                        <div className="flex items-center gap-1 border rounded-md px-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleZoom(-0.1)}
                            >
                                <ZoomOut className="h-4 w-4" />
                            </Button>
                            <span className="text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleZoom(0.1)}
                            >
                                <ZoomIn className="h-4 w-4" />
                            </Button>
                        </div>

                        <Button variant="outline" size="sm" onClick={resetView}>
                            <Maximize2 className="h-4 w-4 mr-1" />
                            Reset
                        </Button>
                        <Button variant="outline" size="sm" onClick={autoArrange}>
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Auto-arrange
                        </Button>

                        {/* System tables toggle */}
                        <div className="flex items-center gap-2 border rounded-md px-3 py-1.5">
                            <Database className="h-4 w-4 text-muted-foreground" />
                            <Label htmlFor="show-system" className="text-sm cursor-pointer">
                                System Tables
                            </Label>
                            <Switch
                                id="show-system"
                                checked={showSystemTables}
                                onCheckedChange={setShowSystemTables}
                            />
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0 h-full overflow-hidden">
                <div
                    ref={containerRef}
                    className="relative w-full h-full overflow-hidden bg-muted/30 cursor-grab"
                    style={{ cursor: isPanning ? 'grabbing' : draggingTable ? 'move' : 'grab' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                >
                    {/* Grid pattern background */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            backgroundImage: `
                                linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px),
                                linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)
                            `,
                            backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
                            backgroundPosition: `${pan.x}px ${pan.y}px`,
                            opacity: 0.3,
                        }}
                    />

                    {/* CSS for dash animation */}
                    <style>
                        {`
                            @keyframes dashMove {
                                from { stroke-dashoffset: 24; }
                                to { stroke-dashoffset: 0; }
                            }
                        `}
                    </style>

                    {/* SVG for relation lines */}
                    <svg
                        ref={svgRef}
                        className="absolute pointer-events-none"
                        style={{
                            left: 0,
                            top: 0,
                            width: '10000px',
                            height: '10000px',
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                            transformOrigin: '0 0',
                            overflow: 'visible',
                            zIndex: 10,
                        }}
                    >
                        <defs>
                            <marker
                                id="arrowhead"
                                markerWidth="10"
                                markerHeight="7"
                                refX="9"
                                refY="3.5"
                                orient="auto"
                            >
                                <polygon
                                    points="0 0, 10 3.5, 0 7"
                                    fill="hsl(var(--primary))"
                                />
                            </marker>
                            <marker
                                id="arrowhead-muted"
                                markerWidth="10"
                                markerHeight="7"
                                refX="9"
                                refY="3.5"
                                orient="auto"
                            >
                                <polygon
                                    points="0 0, 10 3.5, 0 7"
                                    fill="hsl(var(--muted-foreground))"
                                    opacity="0.6"
                                />
                            </marker>
                            <marker
                                id="crowfoot"
                                markerWidth="12"
                                markerHeight="12"
                                refX="10"
                                refY="6"
                                orient="auto"
                            >
                                <path
                                    d="M 0 6 L 10 0 M 0 6 L 10 6 M 0 6 L 10 12"
                                    fill="none"
                                    stroke="hsl(var(--primary))"
                                    strokeWidth="1.5"
                                />
                            </marker>
                            <marker
                                id="crowfoot-muted"
                                markerWidth="12"
                                markerHeight="12"
                                refX="10"
                                refY="6"
                                orient="auto"
                            >
                                <path
                                    d="M 0 6 L 10 0 M 0 6 L 10 6 M 0 6 L 10 12"
                                    fill="none"
                                    stroke="hsl(var(--muted-foreground))"
                                    strokeWidth="1.5"
                                    opacity="0.6"
                                />
                            </marker>
                            {/* Circle for "one" side of relationship */}
                            <marker
                                id="one-circle"
                                markerWidth="8"
                                markerHeight="8"
                                refX="4"
                                refY="4"
                                orient="auto"
                            >
                                <circle cx="4" cy="4" r="3" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
                            </marker>
                            <marker
                                id="one-circle-muted"
                                markerWidth="8"
                                markerHeight="8"
                                refX="4"
                                refY="4"
                                orient="auto"
                            >
                                <circle cx="4" cy="4" r="3" fill="none" stroke="hsl(var(--muted-foreground))" strokeWidth="1.5" opacity="0.6" />
                            </marker>
                        </defs>

                        {relationLines.map((line, index) => {
                            const fromTable = filteredTables.find(t => t.collection === line.from.table);
                            const toTable = filteredTables.find(t => t.collection === line.to.table);

                            if (!fromTable || !toTable) return null;

                            const lineData = getLinePath(fromTable, line.from.field, toTable, line.to.field);
                            const isHighlighted = selectedTable === line.from.table || selectedTable === line.to.table;

                            // Get relationship label
                            const startLabel = '1';
                            const endLabel = line.type === 'one-to-many' ? 'N' : line.type === 'many-to-many' ? 'N' : '1';

                            return (
                                <g key={index}>
                                    {/* Connection line with animation when highlighted */}
                                    <path
                                        d={lineData.path}
                                        fill="none"
                                        stroke={isHighlighted ? '#ec4899' : '#6b7280'}
                                        strokeWidth={isHighlighted ? 2.5 : 1.5}
                                        opacity={isHighlighted ? 1 : 0.5}
                                        strokeDasharray={isHighlighted ? '8 4' : 'none'}
                                        className={isHighlighted ? 'animate-dash' : ''}
                                        style={isHighlighted ? {
                                            animation: 'dashMove 0.5s linear infinite',
                                        } : {}}
                                    />
                                    {/* Start point circle with "1" label */}
                                    <circle
                                        cx={lineData.startX}
                                        cy={lineData.startY}
                                        r={isHighlighted ? 12 : 10}
                                        fill={isHighlighted ? '#ec4899' : '#374151'}
                                        stroke={isHighlighted ? '#ec4899' : '#6b7280'}
                                        strokeWidth={2}
                                    />
                                    <text
                                        x={lineData.startX}
                                        y={lineData.startY}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fill="white"
                                        fontSize={isHighlighted ? 11 : 10}
                                        fontWeight="bold"
                                    >
                                        {startLabel}
                                    </text>
                                    {/* End point circle with "N" or "1" label */}
                                    <circle
                                        cx={lineData.endX}
                                        cy={lineData.endY}
                                        r={isHighlighted ? 12 : 10}
                                        fill={isHighlighted ? '#ec4899' : '#374151'}
                                        stroke={isHighlighted ? '#ec4899' : '#6b7280'}
                                        strokeWidth={2}
                                    />
                                    <text
                                        x={lineData.endX}
                                        y={lineData.endY}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fill="white"
                                        fontSize={isHighlighted ? 11 : 10}
                                        fontWeight="bold"
                                    >
                                        {endLabel}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>

                    {/* Table nodes */}
                    <div
                        className="absolute inset-0"
                        style={{
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                            transformOrigin: '0 0',
                        }}
                    >
                        {filteredTables.map((table) => {
                            const pos = tablePositions[table.collection] || table.position;
                            const isSelected = selectedTable === table.collection;
                            const isConnected = connectionInfo.tables.has(table.collection);
                            const tableHeight = HEADER_HEIGHT + table.fields.length * ROW_HEIGHT;
                            const highlightedFieldsForTable = connectionInfo.fields.get(table.collection);

                            return (
                                <div
                                    key={table.collection}
                                    className={cn(
                                        "absolute select-none rounded-lg border bg-card shadow-md overflow-hidden transition-all duration-200",
                                        isSelected && "ring-2 ring-pink-500 shadow-lg shadow-pink-500/20",
                                        isConnected && "ring-2 ring-pink-400 shadow-lg shadow-pink-400/20",
                                        draggingTable === table.collection && "shadow-xl",
                                        selectedTable && !isSelected && !isConnected && "opacity-40"
                                    )}
                                    style={{
                                        left: pos.x,
                                        top: pos.y,
                                        width: TABLE_WIDTH,
                                        minHeight: tableHeight,
                                    }}
                                    onMouseDown={(e) => handleTableMouseDown(e, table.collection)}
                                    onClick={() => setSelectedTable(isSelected ? null : table.collection)}
                                >
                                    {/* Table header */}
                                    <div
                                        className="flex items-center gap-2 px-3 bg-primary text-primary-foreground font-medium"
                                        style={{ height: HEADER_HEIGHT }}
                                    >
                                        <Grid3X3 className="h-4 w-4 shrink-0" />
                                        <span className="truncate">{table.label}</span>
                                        <Badge variant="secondary" className="ml-auto text-xs bg-primary-foreground/20 text-primary-foreground">
                                            {table.fields.length}
                                        </Badge>
                                    </div>

                                    {/* Fields */}
                                    <div className="divide-y divide-border">
                                        {table.fields.map((field) => {
                                            const isFieldHighlighted = highlightedFieldsForTable?.has(field.name);
                                            return (
                                                <div
                                                    key={field.name}
                                                    className={cn(
                                                        "flex items-center gap-2 px-3 text-sm hover:bg-muted/50 transition-colors",
                                                        field.isPrimary && "bg-yellow-500/10",
                                                        field.isForeignKey && "bg-blue-500/10",
                                                        isFieldHighlighted && "bg-pink-500/30 ring-1 ring-pink-500"
                                                    )}
                                                    style={{ height: ROW_HEIGHT }}
                                                >
                                                    {/* Field icon */}
                                                    <div className="shrink-0 w-4">
                                                        {field.isPrimary ? (
                                                            <Key className="h-3.5 w-3.5 text-yellow-600" />
                                                        ) : field.isForeignKey ? (
                                                            <LinkIcon className="h-3.5 w-3.5 text-blue-600" />
                                                        ) : null}
                                                    </div>

                                                    {/* Field name */}
                                                    <span className={cn(
                                                        "flex-1 truncate",
                                                        field.isPrimary && "font-medium"
                                                    )}>
                                                        {field.name}
                                                    </span>

                                                    {/* Nullable indicator */}
                                                    {field.isNullable && (
                                                        <span className="text-muted-foreground text-xs">?</span>
                                                    )}

                                                    {/* Field type */}
                                                    <span className="text-xs text-muted-foreground font-mono">
                                                        {field.type}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>



                    {/* Table count */}
                    <div className="absolute bottom-4 right-4 bg-card border rounded-lg px-3 py-1.5 shadow-sm text-xs text-muted-foreground">
                        {filteredTables.length} table{filteredTables.length !== 1 ? 's' : ''}
                        {searchQuery && ` matching "${searchQuery}"`}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
